import React, { useMemo } from 'react';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import { ResponsiveAutocomplete } from '@/components/selects/ResponsiveAutocomplete';
import { useListAllFeatureFlagsQuery } from '../../featureFlags/api/featureFlagsApi';
import { getFeatureFlagSourceChipProps } from '../chips/FeatureFlagChip';
import { FEATURE_FLAG_PICKER_SEARCH_OPTIONS } from '../searchOptions';
import { ADMIN_PICKER_OPTION_LIMIT } from './constants';
import { fuseFilterOptions } from './fuseFilterOptions';

const filterFeatureFlags = fuseFilterOptions(FEATURE_FLAG_PICKER_SEARCH_OPTIONS);

/**
 * Pick feature flags from every flag, by feature name, searchable by feature and
 * description, listing at most ADMIN_PICKER_OPTION_LIMIT matches at a time. value/onChange
 * are flag ids (an array when multiple, the default). Options are { id, name: feature,
 * description, flag }, so isOptionDisabled / isOptionFixed can check the flag.
 *
 * A flag that's already on another way shows a small chip on the right, in that source's
 * FeatureFlagChip colour, for the first of: on for everyone ("global"), for ledger
 * ("ledger"), or for any of brokerGroupIds ("broker group"), with "Enabled for …" on hover.
 * Pass the record's broker groups and ledger to check those. Other props go to
 * ResponsiveAutocomplete.
 */
export const AdminFeatureFlagPicker = ({ multiple = true, brokerGroupIds = [], ledger, ...props }) => {
	const { data: flags, isLoading } = useListAllFeatureFlagsQuery();

	const getEnabledBy = (flag) =>
		(flag.active && { source: 'global', label: 'global', tooltip: 'Enabled for everyone' }) ||
		(ledger &&
			(flag.ledgers ?? []).includes(ledger) && {
			source: 'ledger',
			label: 'ledger',
			tooltip: 'Enabled for ledger',
		}) ||
		((flag.brokerGroups ?? []).some((id) => brokerGroupIds.includes(id)) && {
			source: 'brokerGroup',
			label: 'broker group',
			tooltip: 'Enabled for broker group',
		});

	const options = useMemo(
		() =>
			(flags ?? [])
				.map((flag) => ({
					id: flag.id,
					name: flag.feature,
					description: flag.description,
					flag,
				}))
				.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
		[flags],
	);

	return (
		<ResponsiveAutocomplete
			options={options}
			multiple={multiple}
			renderOptionContent={(option) => {
				const enabledBy = getEnabledBy(option.flag);
				return (
					<Stack direction='row' alignItems='center' spacing={1} sx={{ flex: 1, minWidth: 0 }}>
						<Typography sx={{ flex: 1 }}>{option.name}</Typography>
						{enabledBy && (
							<Tooltip title={enabledBy.tooltip} disableInteractive>
								<Chip
									size='small'
									label={enabledBy.label}
									{...getFeatureFlagSourceChipProps(enabledBy.source)}
								/>
							</Tooltip>
						)}
					</Stack>
				);
			}}
			searchable
			filterOptions={filterFeatureFlags}
			optionLimit={ADMIN_PICKER_OPTION_LIMIT}
			loading={isLoading}
			disabled={isLoading}
			{...props}
		/>
	);
};
